import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';

// import { ResetPriceCalculator } from '../../../services/reset-price-calculator/reset-price-calculator.service';
import { ResetPriceCalculator } from 'projects/shared/src/public-api';
import { DavaAddressInfo } from '../../../models/dava.model';
import { DmrResponse } from '../../../models/dmr.model';
import { HelvetiaVariant } from '../../../models/helvetia.model';
import { KsfService } from '../../../services/ksf/ksf.service';
import { LibUtilityService } from '../../../utils';
import {
  KsfAcceptConditions,
  KsfCoverage,
  ksfCustomerInfo,
  ksfExcessTab,
  UpdateKsfTemplate,
} from '../../../models/ksf.model';

@Component({
  selector: 'lib-ksf-base',
  templateUrl: './ksf-base.component.html',
  styleUrls: ['./ksf-base.component.scss'],
  standalone: false,
})
export class KsfBaseComponent {
  // Event emitted to the parent component
  @Output() pickKsfEvent = new EventEmitter();
  @Output() nextStepEvent = new EventEmitter();
  @Output() setSelectedHelvetiaVariantEvent =
    new EventEmitter<HelvetiaVariant>();
  @Output() setKsfPriceResultEvent = new EventEmitter<any>();
  @Output() togglePriceIntervalEvent = new EventEmitter();
  @Output() resetEvent = new EventEmitter();
  @Output() pickHelvetiaEvent = new EventEmitter<boolean>();
  @Output() pickSjfEvent = new EventEmitter<boolean>();
  @Output() setCustomerInfoEvent = new EventEmitter<ksfCustomerInfo>();

  // Input properties to receive data from the parent component
  @Input() activeStep: number = 0;
  @Input() errorIngetInsurancePrices: boolean = false;
  @Input() showKsfInfo: boolean = false;
  @Input() errorInKSFPrice: boolean = false;
  @Input() allHaveTogglePricetag: boolean = false;
  @Input() allHaveToggleChargingStandAllowed: boolean = false;
  @Input() carOwnershipType: string = '';
  @Input() ksfPrice: number = 0;
  @Input() vehicleFromDmr: DmrResponse = {} as DmrResponse;
  @Input() ksfExcessTab: ksfExcessTab = {
    value: 0,
    valueText: '',
  };
  @Input() ksfPriceResult: any; // make interface
  @Input() ksfTemplate: UpdateKsfTemplate = {} as UpdateKsfTemplate;
  @Input() showYearlyPrice: boolean = false;
  @Input() userId: number = 0;
  @Input() dealerId: number | null = null;
  @Input() leadOrigin: number = 2;
  @Input() textDirectedToCustomer: boolean = false;
  @Input() selectedPrice: { name: string; value: number } = {
    name: '',
    value: 0,
  };

  // Forsia input need to show altenative Forsia price if KFS cant/ wont show a price
  @Input() errorInSJFPrice: boolean = false;
  @Input() forsiaExcessPrices: any; // TODO: set type
  @Input() forsiaPrices: any;
  @Input() selectedforsiaExcessIndex: number = 0;
  @Input() forsiaTotalPrice: number = 0;
  forsiaPriceLoading: boolean = false;
  recalculateForsiaPrice() {}

  // Helvetia input need to show altenative Helvetia price if KFS cant/ wont show a price
  @Input() errorInHelvetiaPrice: boolean = false;
  @Input() helvetiaMonthlyPrice: number = 0;
  @Input() helvetiaYearlyPrice: number = 0;
  @Input() helvetiaVariants: HelvetiaVariant[] = [];
  @Input() selectedHelvetiaVariant: HelvetiaVariant = {} as HelvetiaVariant;

  // Internal state variables
  ksfPriceLoading: boolean = false;
  showClauses: boolean = false;
  showWarring: boolean = false;
  chargingStand: boolean = false;
  ksfCoverages: KsfCoverage[] = [];
  ksfCustomerInfo: ksfCustomerInfo = {
    cprNumber: '',
    firstname: '',
    lastname: '',
    selectedAddressInfo: {} as DavaAddressInfo,
    email: '',
    phoneNumber: '',
  };

  ksfAcceptConditions: KsfAcceptConditions = {
    acceptedTradingConditions: false,
    acceptedInfoIsCorrect: false,
    acceptedConsent: false,
    acceptedAsgMarketing: false,
    acceptedChargingStandConsultant: false,
    acceptedChargingStandCustomer: false,
  };

  // Is usesd to show / hide tooltip based on the tilvalg index
  showTooltipIndexArray: boolean[] = [];
  // Is usesd to show / hide field based on the field index - we do this so we dont show all field at once, but one by one as the former i filled out
  showFieldIndexArray: boolean[] = [];
  // Is usesd to show the slected values in the dropdowns
  selectedFieldsValues: any[] = [];

  constructor(
    private ksfService: KsfService,
    private resetPriceCalculator: ResetPriceCalculator,
    private libUtilityService: LibUtilityService,
  ) {
    // Subscribe to reset events
    this.resetPriceCalculator.resetPriceCalculatorValues.subscribe(() => {
      this.resetKsfInfo();
    });

    this.resetPriceCalculator.resetKsfValues.subscribe(() => {
      this.resetKsfInfo();
    });
  }

  /**
   * Emit reset event
   * @returns void
   */
  reset(): void {
    this.resetEvent.emit();
  }

  /**
   *  Reset KSF information and related flags
   * @returns void
   */
  resetKsfInfo(): void {
    this.showClauses = false;
    this.showWarring = false;
    this.chargingStand = false;
    this.ksfCoverages = [];

    this.ksfCustomerInfo = {
      cprNumber: '',
      firstname: '',
      lastname: '',
      selectedAddressInfo: {} as DavaAddressInfo,
      email: '',
      phoneNumber: '',
    };

    this.ksfAcceptConditions = {
      acceptedTradingConditions: false,
      acceptedInfoIsCorrect: false,
      acceptedConsent: false,
      acceptedAsgMarketing: false,
      acceptedChargingStandConsultant: false,
      acceptedChargingStandCustomer: false,
    };

    this.setCustomerInfoEvent.emit(this.ksfCustomerInfo);
  }

  setCustomerInfo(customerInfo: ksfCustomerInfo): void {
    this.ksfCustomerInfo = customerInfo;
    this.setCustomerInfoEvent.emit(this.ksfCustomerInfo);
  }

  /**
   * Respond to changes in input properties.
   * Updates the visibility of clauses and tooltips based on changes to `ksfPriceResult` and `selectedHelvetiaVariant`.
   * Initializes `showFieldIndexArray` and `showTooltipIndexArray` based on the new `ksfPriceResult` data.
   * @param changes - An object that holds the changes for the input properties.
   * @returns void
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.showClauses = false;

    if (!changes['selectedHelvetiaVariant']) {
      this.showWarring = false;
    }

    if (
      changes['ksfPriceResult'] &&
      this.ksfPriceResult &&
      this.ksfPriceResult.covers &&
      this.ksfPriceResult.fields
    ) {
      // Set what tooltips to show
      this.showTooltipIndexArray = new Array(
        this.ksfPriceResult.covers.length,
      ).fill(false);

      // Set what fields to show
      if (this.showFieldIndexArray.length == 0) {
        this.showFieldIndexArray = new Array(
          this.ksfPriceResult.fields.length,
        ).fill(false);
      }
    }
  }

  /**
   * Emit event to move to the next step in the KSF flow
   * @param goToNextStep - A boolean indicating whether to proceed to the next step after picking the price.
   * @returns void
   */
  pickKsf(goToNextStep: boolean): void {
    this.pickKsfEvent.emit(goToNextStep);
  }

  /**
 * Proceed to the next step or fetch cover prices if on step 3.
   Emit event to move to the next step
 * @returns void
 */
  nextStep(): void {
    if (this.activeStep == 3) {
      /// CALL getCoverPrices, with all covers selected JUST to get the cover prices to display
      this.getCoverPrices();
    } else {
      this.nextStepEvent.emit();
    }
  }

  /**
   * Emit event to toggle the price interval display
   * @returns void
   */
  togglePriceInterval(): void {
    this.togglePriceIntervalEvent.emit();
  }

  /**
   * Recalculates the KSF price by updating the KSF template and recalculating the total cover premium and associated taxes and fees.
   * Updates the KSF price result and emits an event with the updated template.
   * @returns void
   */
  recalculateKsfPrice(): void {
    this.ksfPriceLoading = true;

    this.ksfService.updateKsfTemplate(this.ksfTemplate).subscribe({
      next: (ksfPriceResult: any) => {
        this.ksfPriceResult = ksfPriceResult.template;
        this.setKsfPriceResultEvent.emit(this.ksfPriceResult);

        this.ksfPrice =
          this.calculateTotalCoverPremium() +
          this.libUtilityService.getKsfTaxAndFeesPrice(this.ksfPriceResult);

        this.ksfPriceLoading = false;
      },
      error: (error) => {
        this.ksfPriceLoading = false;
        this.errorIngetInsurancePrices = true;
        this.libUtilityService.notification(
          'error',
          'Fejl',
          'Der er sket en fejl - Kontakt os på support@asgcare.dk',
        );
        return;
      },
    });
  }

  /**
   * Calculates the total premium for all selected covers.
   * Filters the covers to include only those that are selected and sums their premiums.
   * @returns number - The total price for the selected covers.
   */
  calculateTotalCoverPremium(): number {
    return this.ksfPriceResult.covers
      .filter((cover: any) => cover.selected)
      .reduce(
        (sum: number, cover: { coverPremium: any }) => sum + cover.coverPremium,
        0,
      );
  }

  /**
   * Retrieves the prices for all covers by updating the KSF template with all covers selected.
   * Resets the selected state of all covers after fetching the prices and proceeds to the next step.
   * @returns void
   */
  getCoverPrices(): void {
    this.ksfPriceLoading = true;

    // Make a new template where all covers are selected
    const getCoverPricesTemplate = { ...this.ksfTemplate };

    getCoverPricesTemplate.covers = this.ksfPriceResult.covers.map(
      (cover: any) => ({
        Id: `${cover.id}`,
        Selected: true,
      }),
    );

    // Fetch prices of covers by using the template copy with all covers set to true so we can show the prices
    this.ksfService.updateKsfTemplate(getCoverPricesTemplate).subscribe({
      next: (ksfPriceResult: any) => {
        this.ksfCoverages = ksfPriceResult.template.covers;

        // Update the template at KF with covers set to false so the data we're referencing in the template is the same - before moving on to actually choose covers
        this.ksfService.updateKsfTemplate(this.ksfTemplate).subscribe({
          next: (ksfPriceResult: any) => {
            this.nextStepEvent.emit();
            this.ksfPriceLoading = false;
          },
          error: () => {
            this.ksfPriceLoading = false;
            this.errorIngetInsurancePrices = true;
            this.libUtilityService.notification(
              'error',
              'Fejl',
              'Der er sket en fejl - Kontakt os på support@asgcare.dk',
            );
            return;
          },
        });
      },
      error: () => {
        this.ksfPriceLoading = false;
        this.errorIngetInsurancePrices = true;
        this.libUtilityService.notification(
          'error',
          'Fejl',
          'Der er sket en fejl - Kontakt os på support@asgcare.dk',
        );
        return;
      },
    });
  }

  /**
   * Sets the selected Helvetia variant and emits an event with the updated variant.
   * This function is used when the Helvetia price card is disaply in <ksf-warrings-dispaly>.
   * @param helvetiaVariant - The selected Helvetia variant to be set.
   * @returns void
   */
  setSelectedHelvetiaVariant(helvetiaVariant: HelvetiaVariant): void {
    this.selectedHelvetiaVariant = helvetiaVariant;
    this.setSelectedHelvetiaVariantEvent.emit(this.selectedHelvetiaVariant);
  }
}
